import {
    type PriceCalculationUnit,
    type PriceCalculationValueWithUnit,
    type PriceCalculationUnitValueType,
    type BoundMultiValueValue,
    type MultiValueOption,
    type ParameterValueKind,
    type ServiceDefinition,
    PriceCalculationParameterInfo,
} from "../api";
import { resolveOptionsValue } from "./resolve-options-value";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a resolved multi-value option.
 */
export type ResolvedMultiValueOption = MultiValueOption & {
    calculationValue?: PriceCalculationUnitValueType;
};

/**
 * Represents a multi-value selection in a parameter value.
 *
 * This value object holds options with IDs, values, and labels bound to a parameter,
 * allowing multiple selections.
 */
export class ResolvedMultiValueValue extends ResolvedParameterValue<string[], BoundMultiValueValue> {
    readonly options: ResolvedMultiValueOption[];
    readonly unit?: PriceCalculationUnit;
    readonly aggregation?: "sum" | "product";

    /**
     * Creates a new multi-value parameter.
     *
     * @param value The options
     */
    constructor(value: Omit<BoundMultiValueValue, "kind">) {
        super({
            ...value,
            kind: "multi-value",
        });
        this.options = value.options?.map(it => resolveOptionsValue(it)) ?? [];
        this.aggregation = value.aggregation;
        this.unit = value.unit;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the options.
     *
     * @returns The options
     */
    getOptions(): ResolvedMultiValueOption[] {
        return this.options;
    }

    /**
     * Gets the unit for the calculation parameter.
     *
     * @returns The unit for the calculation parameter
     */
    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        if (this.unit === undefined) {
            return [];
        } else {
            return [
                {
                    id: PriceCalculationParameterInfo.Id(this.parameterId.toString()),
                    parameterId: this.parameterId,
                    unit: this.unit,
                    combineWithParameters: this.combineWithParameters,
                },
            ];
        }
    }

    /**
     * Gets the value for the calculation parameter.
     *
     * @param value The value of the parameter
     * @returns The value for the calculation parameter
     */
    getCalculationParameterValues(
        value: string[] | string | undefined
    ): Record<string, PriceCalculationValueWithUnit> {
        if (this.unit === undefined) {
            return {};
        }

        if (value === undefined) {
            if (this.aggregation === "product") {
                return { [this.parameterId.toString()]: { value: 1, unit: this.unit } };
            }
            return { [this.parameterId.toString()]: { value: 0, unit: this.unit } };
        }

        // Normalize single string value to array for multi-value parameters
        const normalizedValue: string[] = Array.isArray(value) ? value : [value];

        if (this.aggregation === "sum" || this.aggregation === undefined) {
            const sum = normalizedValue.reduce(
                (sum, val) => sum + (this.options.find(it => it.value === val)?.calculationValue ?? 0),
                0
            );
            return { [this.parameterId.toString()]: { value: sum, unit: this.unit } };
        }
        if (this.aggregation === "product") {
            const product = normalizedValue.reduce(
                (product, val) =>
                    product * (this.options.find(it => it.value === val)?.calculationValue ?? 1),
                1
            );
            return { [this.parameterId.toString()]: { value: product, unit: this.unit } };
        }
        return {};
    }

    /**
     * Gets the view information for the parameter.
     *
     * @param values The value of the parameter
     * @returns The view information for the parameter
     */
    getViewText(values: string[] | undefined): string | undefined {
        if (values === undefined) {
            return undefined;
        }
        return values
            .map(it => this.options.find(option => option.value === it)?.label)
            .filter(it => it !== undefined)
            .join(", ");
    }

    /**
     * Creates a customer-safe version of this multi-value.
     *
     * @param requestPricing - The pricing mode of the service definition
     * @returns For "show-price" and "show-disclosed-price": this instance
     *          For "show-no-price": new instance without calculationValue and combineWithParameters
     */
    toCustomerValue(requestPricing: ServiceDefinition.RequestPricing): ResolvedMultiValueValue {
        if (requestPricing !== "show-no-price") {
            return this;
        }
        return new ResolvedMultiValueValue({
            parameterId: this.parameterId,
            value: this.value,
            options: this.options.map(({ calculationValue: _calculationValue, ...rest }) => rest),
            unit: this.unit,
            aggregation: this.aggregation,
        });
    }

    /**
     * Creates a new multi-value parameter with valid options.
     *
     * @param value The options bound to a parameter
     * @returns A new multi-value parameter or never if the options are invalid
     */
    static create(value: Omit<BoundMultiValueValue, "kind">): ResolvedMultiValueValue | never {
        const { options } = value;
        const optionValues = options?.map(it => it.value).filter(it => it !== undefined) ?? [];
        if (hasDuplicates(optionValues)) {
            throw new Error(
                "MultiValueOption has values with duplicate a 'value'. Property 'value' must be unique."
            );
        }
        optionValues.forEach(value => {
            if (value === "") {
                throw new Error("MultiValueOption: Property 'value' of value cannot be empty.");
            }
        });

        if (hasDuplicates(options?.map(it => it.label).filter(it => it !== undefined) ?? [])) {
            throw new Error(
                "MultiValueOption has values with duplicate a 'label'. Property 'label' must be unique."
            );
        }

        if (value.value !== undefined) {
            value.value.forEach(v => {
                if (!optionValues.includes(v)) {
                    throw new Error(
                        "MultiValueOption: default values must be a subset of the defined option values."
                    );
                }
            });
        }

        return new ResolvedMultiValueValue(value);
    }
}

const hasDuplicates = (array: string[]) => new Set(array).size !== array.length;
